import React from 'react';
import {
  AutoStories,
  BookmarkAdd,
  CheckCircleOutline,
  EditNote,
  FavoriteBorder,
  FormatListBulleted,
  Palette,
  PersonAddAlt1,
} from '@mui/icons-material';

export type CardKind = 'onboarding' | 'feature';

export interface CardEntry {
  id: string;
  kind: CardKind;
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaLabel: string;
  onAction: () => void;
  /** Card auto-hides when this returns true. */
  isCompleted: () => boolean;
}

/**
 * Snapshot of the user-state values each default card needs to decide
 * completion / fire its CTA. Passing it as a single object means adding a card
 * only ever needs touching this file, not HomeCardStack.
 */
export interface HomeCardContext {
  wantToWatchCount: number;
  watchedCount: number;
  reviewedCount: number;
  watchlistCount: number;
  likedListCount: number;
  followedCount: number;
  hasPostedStory: boolean;
  /** Current cinema theme id; 'celluloid' is the default. */
  cinemaTheme: string;
  /** True once the user has explicitly picked any theme via the settings screen. */
  hasChosenTheme: boolean;
  navigate: (path: string) => void;
  /**
   * Dispatcher for window events the AppLayout listens to (CreateBottomSheet,
   * AddStoryBottomSheet). Keeps cards decoupled from dialog state.
   */
  emit: (event: 'home-card:create' | 'home-card:story') => void;
}

export function buildDefaultCards(ctx: HomeCardContext): CardEntry[] {
  return [
    // ────────────────────────── Onboarding (push first actions) ──────────────────────────
    {
      id: 'save-first-movie',
      kind: 'onboarding',
      icon: <BookmarkAdd sx={{ fontSize: 20 }} />,
      title: 'Save your first movie',
      description: "Click on a movie and 'Add to Watchlist'",
      ctaLabel: 'Find a movie',
      onAction: () => ctx.navigate('/search'),
      isCompleted: () => ctx.wantToWatchCount > 0,
    },
    {
      id: 'mark-first-watched',
      kind: 'onboarding',
      icon: <CheckCircleOutline sx={{ fontSize: 20 }} />,
      title: 'Mark as watched',
      description: 'Rate or review',
      ctaLabel: 'Find a movie',
      onAction: () => ctx.navigate('/search'),
      isCompleted: () => ctx.watchedCount > 0,
    },
    {
      id: 'post-first-review',
      kind: 'onboarding',
      icon: <EditNote sx={{ fontSize: 20 }} />,
      title: 'Post your first review',
      description: 'Share your thoughts',
      ctaLabel: 'Find a movie',
      onAction: () => ctx.navigate('/search'),
      isCompleted: () => ctx.reviewedCount > 0,
    },
    {
      id: 'add-first-watchlist',
      kind: 'onboarding',
      icon: <FormatListBulleted sx={{ fontSize: 20 }} />,
      title: 'Start a watchlist',
      description: 'Save movies and share lists with your crew.',
      ctaLabel: 'New list',
      onAction: () => ctx.emit('home-card:create'),
      isCompleted: () => ctx.watchlistCount > 0,
    },
    {
      id: 'like-popular-list',
      kind: 'onboarding',
      icon: <FavoriteBorder sx={{ fontSize: 20 }} />,
      title: 'Like a popular list',
      description: 'Save a trending watchlist.',
      ctaLabel: 'Browse trending',
      onAction: () => ctx.navigate('/trending?tab=1'),
      isCompleted: () => ctx.likedListCount > 0,
    },
    {
      id: 'follow-first-crew',
      kind: 'onboarding',
      icon: <PersonAddAlt1 sx={{ fontSize: 20 }} />,
      title: 'Follow a friend',
      description: 'Discover what they are watching',
      ctaLabel: 'Find crew',
      onAction: () => ctx.navigate('/crew/find'),
      isCompleted: () => ctx.followedCount > 0,
    },

    // ────────────────────────── Feature announcements ──────────────────────────
    {
      id: 'try-stories',
      kind: 'feature',
      icon: <AutoStories sx={{ fontSize: 20 }} />,
      title: 'Try Spotlights',
      description: 'Feature a review or list for 7 days.',
      ctaLabel: 'Share a spotlight',
      onAction: () => ctx.emit('home-card:story'),
      isCompleted: () => ctx.hasPostedStory,
    },
    {
      id: 'change-your-theme',
      kind: 'feature',
      icon: <Palette sx={{ fontSize: 20 }} />,
      title: 'Change your theme',
      description: 'Pick a cinema theme that fits your mood.',
      ctaLabel: 'Pick a theme',
      onAction: () => ctx.navigate('/settings'),
      isCompleted: () => ctx.hasChosenTheme,
    },
  ];
}
